import { useState, useEffect, useRef } from 'react';
import { Skill } from '../types';

interface SkillsProps {
  skills: Skill[];
}

interface CircularChartProps {
  name: string;
  percentage: number;
}

function CircularChart({ name, percentage }: CircularChartProps) {
  const [animated, setAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const size = 160;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = animated ? circumference - (percentage / 100) * circumference : circumference;

  return (
    <div ref={ref} className="chart">
      <svg
        width={size}
        height={size}
        style={{ position: 'absolute', left: 0, top: 0 }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f5f5f5"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#006266"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="butt"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <span>
        <strong>{name}</strong>
        {percentage}%
      </span>
    </div>
  );
}

function Skills({ skills }: SkillsProps) {
  const circularSkills = skills.filter((s) => s.type === 'circular');
  const barSkills = skills.filter((s) => s.type === 'bar');
  const leftBarSkills = barSkills.slice(0, 4);
  const rightBarSkills = barSkills.slice(4);

  return (
    <div id="fh5co-skills" className="animate-box">
      <div className="container">
        <div className="row">
          <div className="col-md-8 col-md-offset-2 text-center fh5co-heading">
            <h2>Skills</h2>
          </div>
        </div>
        <div className="row row-pb-md">
          {circularSkills.map((skill) => (
            <div key={skill.name} className="col-md-3 col-sm-6 col-xs-12 text-center">
              <CircularChart name={skill.name} percentage={skill.percentage} />
            </div>
          ))}
        </div>
        <div className="row">
          <div className="col-md-6">
            {leftBarSkills.map((skill) => (
              <div key={skill.name} className="progress-wrap">
                <h3>
                  <span className="name-left">{skill.name}</span>
                  <span className="value-right">{skill.percentage}%</span>
                </h3>
                <div className="progress">
                  <div
                    className={`progress-bar${skill.barClass ? ` ${skill.barClass}` : ''} progress-bar-striped active`}
                    role="progressbar"
                    aria-valuenow={skill.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{ width: `${skill.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="col-md-6">
            {rightBarSkills.map((skill) => (
              <div key={skill.name} className="progress-wrap">
                <h3>
                  <span className="name-left">{skill.name}</span>
                  <span className="value-right">{skill.percentage}%</span>
                </h3>
                <div className="progress">
                  <div
                    className={`progress-bar${skill.barClass ? ` ${skill.barClass}` : ''} progress-bar-striped active`}
                    role="progressbar"
                    aria-valuenow={skill.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{ width: `${skill.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Skills;
